/**
 * Página mostrada tras un pago (o alta add-pet) exitoso.
 * Recibe contexto por `location.state` para mostrar resumen sin fetch.
 */

import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';

const UF_CLP_APPROX = 37000;

export type PaymentSuccessState = {
  /** 'batch' = pago único cobrado ahora. 'add-pet' = alta alineada al ciclo. */
  mode: 'batch' | 'add-pet';
  petCount: number;
  /** Total UF cobrado (batch) o prorrateado (add-pet). 0 si fue free-add completo. */
  totalUf?: number;
  /** Nombres de mascotas cubiertas (opcional). */
  petNames?: string[];
  /** Solo add-pet: al menos una línea cayó en la ventana gratis (<5 días restantes). */
  freeWindow?: boolean;
};

export function PaymentSuccessPage() {
  const location = useLocation();
  const state = (location.state as PaymentSuccessState | null) ?? null;

  const mode = state?.mode ?? 'batch';
  const petCount = state?.petCount ?? 1;
  const totalUf = state?.totalUf ?? 0;
  const totalClp = Math.round(totalUf * UF_CLP_APPROX);
  const freeWindow = state?.freeWindow === true;

  const headline =
    mode === 'batch' ? '¡Pago realizado con éxito!' : '¡Mascota(s) añadidas al contrato!';
  const summary =
    mode === 'batch'
      ? petCount > 1
        ? `${petCount} mascotas quedaron cubiertas con un solo cobro.`
        : 'Tu mascota quedó cubierta.'
      : freeWindow
        ? petCount > 1
          ? `${petCount} mascotas se sumaron a tu contrato sin cobro hoy.`
          : 'Tu mascota se sumó al contrato sin cobro hoy.'
        : petCount > 1
          ? `${petCount} mascotas se sumaron a tu contrato con un cobro prorrateado.`
          : 'Tu mascota se sumó al contrato con un cobro prorrateado.';

  return (
    <div className="min-h-screen flex flex-col bg-[#FFDCE6]">
      <section className="px-4 md:px-8 py-10 md:py-16">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg text-center space-y-5">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>

            <div className="space-y-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{headline}</h1>
              <p className="text-sm text-gray-600">{summary}</p>
            </div>

            <p className="text-sm text-gray-700 bg-[#FFDCE6]/40 border border-[#FFDCE6] rounded-xl p-4">
              Gracias por confiar en <strong>MishiWoof</strong>. Cuidamos a quienes cuidamos juntos.
            </p>

            {mode === 'batch' && totalUf > 0 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-left">
                <p className="text-xs text-gray-500">Monto cobrado (referencia)</p>
                <p className="text-lg font-semibold text-gray-900 tabular-nums">
                  {totalUf.toFixed(2)} UF{' '}
                  <span className="text-gray-500 font-normal text-xs">
                    (~{totalClp.toLocaleString('es-CL')} CLP)
                  </span>
                </p>
              </div>
            )}

            {mode === 'add-pet' && freeWindow && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-left">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                  Gratis con condición de permanencia
                </p>
                <p className="text-sm text-emerald-900 mt-1">
                  Agregaste una mascota dentro de los últimos días del ciclo, así que no hay cobro
                  hoy. Desde el próximo ciclo se facturará el mes completo junto al resto.
                </p>
              </div>
            )}

            {mode === 'add-pet' && !freeWindow && totalUf > 0 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-left">
                <p className="text-xs text-gray-500">Cobro prorrateado</p>
                <p className="text-lg font-semibold text-gray-900 tabular-nums">
                  {totalUf.toFixed(3)} UF{' '}
                  <span className="text-gray-500 font-normal text-xs">
                    (~{totalClp.toLocaleString('es-CL')} CLP)
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Desde el próximo ciclo se cobra el mes completo.
                </p>
              </div>
            )}

            {state?.petNames && state.petNames.length > 0 && (
              <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 text-left">
                <p className="text-xs text-gray-500 mb-1">
                  {state.petNames.length > 1 ? 'Mascotas cubiertas' : 'Mascota cubierta'}
                </p>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {state.petNames.join(', ')}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button asChild className="flex-1 bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white">
                <Link to="/sucursal-virtual">Ir a Mi cuenta</Link>
              </Button>
              <Button asChild variant="outline" className="flex-1">
                <Link to="/mascotas">Mis mascotas</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
